class WishlistUtils {
  static get wishlistId() {
    const cookies = decodeURIComponent(document.cookie).split(';');
    const cookieString = 'wishlist_id=';

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();

      if (cookie.indexOf(cookieString) === 0) {
        return cookie.substring(cookieString.length);
      }
    }

    return null;
  }

  static get customerId() {
    return window.WishlistConfig?.customerId || null;
  }

  static buildWishlistUrl() {
    const customerId = WishlistUtils.customerId;

    if (customerId) {
      return '/a/wishlist?type=api&customerid=' + customerId + '&version=1';
    }

    const wishlistId = WishlistUtils.wishlistId;

    if (! wishlistId) {
      return null;
    }

    return '/a/wishlist?type=api&wishlistid=' + wishlistId + '&version=1';
  }

  static async retrieveWishlist({ force = false } = {}) {
    /** Dedup concurrent calls regardless of `force`: PLP/PDP mount many buttons at once. */
    if (WishlistUtils._inflight) {
      return WishlistUtils._inflight;
    }

    if (! force && WishlistUtils.wishlist) {
      return WishlistUtils.wishlist;
    }

    const url = WishlistUtils.buildWishlistUrl();

    if (! url) {
      /**
       * Guest wishlists often use a cookie that is HttpOnly or not yet readable here.
       * Never wipe `localStorage` in that case — we still persist items from
       * `addToSmartWishlist` and the wishlist page renders from that cache.
       */
      const cached = WishlistUtils.wishlist;

      return Array.isArray(cached) ? cached : [];
    }

    const request = (async () => {
      try {
        const response = await fetch(url, { method: 'GET', cache: 'no-store' });
        const data = await response.json();
        const items = Array.isArray(data.items) ? data.items : [];

        localStorage.setItem('wishlist', JSON.stringify(items));

        return items;
      } catch (error) {
        console.error('Error while fetching wishlist:', error);
        return WishlistUtils.wishlist || [];
      } finally {
        WishlistUtils._inflight = null;
      }
    })();

    WishlistUtils._inflight = request;

    return request;
  }

  static get wishlist() {
    const raw = localStorage.getItem('wishlist');

    if (! raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  static isInWishlist(item) {
    const wishlist = WishlistUtils.wishlist;

    if (! wishlist) {
      return false;
    }

    return wishlist.some(
      (entry) => String(entry.id) === String(item.id) && String(entry.variant_id) === String(item.variant_id)
    );
  }

  static waitForGlobal(name, timeout = 3000) {
    return new Promise((resolve) => {
      if (typeof window[name] === 'function') {
        resolve(true);
        return;
      }

      const start = Date.now();
      const interval = setInterval(() => {
        if (typeof window[name] === 'function') {
          clearInterval(interval);
          resolve(true);
        } else if (Date.now() - start >= timeout) {
          clearInterval(interval);
          resolve(false);
        }
      }, 50);
    });
  }

  static async addToSmartWishlist(item) {
    const ready = await WishlistUtils.waitForGlobal('AddToSmartWishlist');

    if (! ready) {
      console.error('AddToSmartWishlist global is not defined after wait');
      return false;
    }

    try {
      AddToSmartWishlist(item.id, item.variant_id);
    } catch (error) {
      console.error('AddToSmartWishlist failed:', error);
      return false;
    }

    const wishlist = WishlistUtils.wishlist || [];

    if (! WishlistUtils.isInWishlist(item)) {
      wishlist.push({ id: String(item.id), variant_id: String(item.variant_id), handle: item.handle });
      localStorage.setItem('wishlist', JSON.stringify(wishlist));
    }

    return true;
  }

  static async removeFromSmartWishlist(item) {
    const ready = await WishlistUtils.waitForGlobal('RemoveFromSmartWishlist');

    if (ready) {
      try {
        RemoveFromSmartWishlist(item.id, item.variant_id);
      } catch (error) {
        console.error('RemoveFromSmartWishlist failed:', error);
      }
    } else {
      console.warn('RemoveFromSmartWishlist global is not defined after wait; updating local cache only');
    }

    const wishlist = WishlistUtils.wishlist;

    if (! wishlist) {
      return true;
    }

    const next = wishlist.filter(
      (entry) => ! (String(entry.id) === String(item.id) && String(entry.variant_id) === String(item.variant_id))
    );

    localStorage.setItem('wishlist', JSON.stringify(next));

    return true;
  }
}


class RoWishlistButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.pending = false;
    this.skipNextClick = false;
  }

  get item() {
    return {
      id: this.dataset.product,
      variant_id: this.dataset.variant,
      handle: this.dataset.handle,
    };
  }

  get origin() {
    return this.dataset.origin === 'pdp' ? 'pdp' : 'plp';
  }

  get isInWishlist() {
    return WishlistUtils.isInWishlist(this.item);
  }

  connectedCallback() {
    if (! this.dataset.product || ! this.dataset.variant) {
      return;
    }

    WishlistUtils.retrieveWishlist({ force: true }).then(() => this.updateState());

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);

    this.unsubscribeWishlistChange = subscribe(PUB_SUB_EVENTS.wishlistChange, () => this.updateState());

    if (this.origin === 'pdp') {
      this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.unsubscribeWishlistChange && this.unsubscribeWishlistChange();
    this.unsubscribeVariantChange && this.unsubscribeVariantChange();
  }

  /**
   * Activates the wishlist toggle via keyboard (Enter / Space).
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    event.stopPropagation();
    this.skipNextClick = true;
    this.handleClick(event);
  }

  handleVariantChange(payload) {
    const variant = payload?.data?.variant;
    if (! variant || variant.id == null) return;

    this.dataset.variant = String(variant.id);
    this.updateState();
  }

  async handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === 'click' && this.skipNextClick) {
      this.skipNextClick = false;
      return;
    }

    if (this.pending) return;
    this.pending = true;

    const wasInWishlist = this.isInWishlist;
    const item = this.item;
    let mutated = false;

    try {
      if (wasInWishlist) {
        mutated = await WishlistUtils.removeFromSmartWishlist(item);
      } else {
        mutated = await WishlistUtils.addToSmartWishlist(item);
      }
    } finally {
      this.pending = false;
    }

    if (! mutated) {
      return;
    }

    this.updateState();
    publish(PUB_SUB_EVENTS.wishlistChange);

    const isCustomer = !! WishlistUtils.customerId;
    const origin = this.origin;

    if (wasInWishlist) {
      if (origin === 'pdp') {
        publish(isCustomer ? PUB_SUB_EVENTS.customerWishlistProductItemRemoved : PUB_SUB_EVENTS.wishlistProductItemRemoved);
      } else {
        publish(isCustomer ? PUB_SUB_EVENTS.customerWishlistCollectionItemRemoved : PUB_SUB_EVENTS.wishlistCollectionItemRemoved);
      }
    } else {
      if (origin === 'pdp') {
        publish(isCustomer ? PUB_SUB_EVENTS.customerWishlistProductItemAdded : PUB_SUB_EVENTS.wishlistProductItemAdded);
      } else {
        publish(isCustomer ? PUB_SUB_EVENTS.customerWishlistCollectionItemAdded : PUB_SUB_EVENTS.wishlistCollectionItemAdded);
      }
    }
  }

  updateState() {
    const target = this.querySelector('[data-bookmark-target]') || this;
    target.classList.toggle('bookmarked', this.isInWishlist);
  }
}

customElements.define('ro-wishlist-button', RoWishlistButton);


class RoWishlistIcon extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    /** First mount: force-pull fresh data from the app proxy. */
    this.refresh({ force: true });

    /** Subsequent updates: read cache only, mutations already updated `localStorage` synchronously. */
    this.unsubscribeWishlistChange = subscribe(PUB_SUB_EVENTS.wishlistChange, () => this.refresh());
    this.unsubscribeWishlistLoaded = subscribe(PUB_SUB_EVENTS.wishlistLoaded, () => this.refresh());
    this.unsubscribeWishlistUpdated = subscribe(PUB_SUB_EVENTS.wishlistUpdated, () => this.refresh());
  }

  disconnectedCallback() {
    this.unsubscribeWishlistChange && this.unsubscribeWishlistChange();
    this.unsubscribeWishlistLoaded && this.unsubscribeWishlistLoaded();
    this.unsubscribeWishlistUpdated && this.unsubscribeWishlistUpdated();
  }

  async refresh({ force = false } = {}) {
    const items = await WishlistUtils.retrieveWishlist({ force });
    const count = items.length || 0;
    const target = this.querySelector('[data-count]') || this;

    target.innerText = count > 0 ? count : '';
  }
}

customElements.define('ro-wishlist-icon', RoWishlistIcon);
